import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import BaseTypeSection from './BaseTypeSection';
import './MaintenanceTemplatesEditor.css';
import { ComponentLifetime, TemplatesByType } from "./types";
import { BaseType } from "../../../types/Enums";
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { RELEVANT_BASE_TYPES } from '../../../Helpers/baseTypeUtils';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../../Components/BreadCrumb';

interface MaintenanceTemplatesEditorProps {
    /** Rendered as a tab inside another page, which has its own breadcrumb. */
    embedded?: boolean;
}

const MaintenanceTemplatesEditor: React.FC<MaintenanceTemplatesEditorProps> = ({ embedded = false }) => {
    const { types: buildComponentTypes, getLifetime, getTemplates } = useBuildComponentTypes();

    // Set document title
    useDocumentTitle('Underhållsmallar');

    const [templates, setTemplates] = useState<TemplatesByType>({});
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [expandedBaseSections, setExpandedBaseSections] = useState<Set<string>>(new Set());
    const [expandedComponentSections, setExpandedComponentSections] = useState<Set<string>>(new Set());
    const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
    const [editingLifetime, setEditingLifetime] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const db = getDatabase();

    // Load templates and lifetimes from context
    useEffect(() => {
        try {
            setLoading(true);
            
            const templatesByType: TemplatesByType = {};
            const lifetimesByType: Record<string, ComponentLifetime> = {};
            
            // Initialize from buildComponentTypes context
            Object.keys(buildComponentTypes).forEach((type: string) => {
                // Get templates from context
                const typeTemplates = getTemplates(type);
                templatesByType[type] = typeTemplates;
                
                // Get lifetime from context
                const lifetime = getLifetime(type);
                if (lifetime) {
                    lifetimesByType[type] = lifetime;
                }
            });

            setTemplates(templatesByType);
            setComponentLifetimes(lifetimesByType);
        } catch (error) {
            console.error('Error loading templates and lifetimes:', error);
        } finally {
            setLoading(false);
        }
    }, [buildComponentTypes, getTemplates, getLifetime]);

    const toggleBaseSection = (baseType: string): void => {
        const newExpanded = new Set(expandedBaseSections);
        if (newExpanded.has(baseType)) newExpanded.delete(baseType);
        else newExpanded.add(baseType);
        setExpandedBaseSections(newExpanded);
    };

    const toggleComponentSection = (componentType: string): void => {
        const newExpanded = new Set(expandedComponentSections);
        if (newExpanded.has(componentType)) newExpanded.delete(componentType);
        else newExpanded.add(componentType);
        setExpandedComponentSections(newExpanded);
    };

    const getBaseTypeTemplateCount = (baseType: BaseType): number => {
        const componentTypes = Object.entries(buildComponentTypes)
            .filter(([_, def]) => def.baseType === baseType)
            .map(([type]) => type);
        return componentTypes.reduce((total, componentType) => total + (templates[componentType]?.length || 0), 0);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Loading templates...</h5>
                </div>
            </div>
        );
    }

    const relevantBaseTypes = RELEVANT_BASE_TYPES;

    return (
        <>
        {!embedded && <BreadcrumbComponent items={[{ text: 'Underhållsmallar' }]} />}
        <div className="maintenance-templates-editor" >
            {/* Header */}
            <div style={{ marginBottom: '30px' }}>
                <h1 className="page-title">Underhållsmallar</h1>
                <p className="text-muted">Hantera mallar för återkommande underhåll och beräknad livslängd</p>
            </div>

            {/* Main Content */}
            <div className="main-content-templates-editor" style={{ marginLeft: '0px' }}>
                <div className="accordion" id="maintenanceAccordion">
                    {Object.values(relevantBaseTypes).map((baseType: BaseType) => (
                        <BaseTypeSection
                            key={baseType}
                            baseType={baseType}
                            templates={templates}
                            setTemplates={setTemplates}
                            componentLifetimes={componentLifetimes}
                            setComponentLifetimes={setComponentLifetimes}
                            expandedBaseSections={expandedBaseSections}
                            expandedComponentSections={expandedComponentSections}
                            editingTemplate={editingTemplate}
                            editingLifetime={editingLifetime}
                            saving={saving}
                            toggleBaseSection={toggleBaseSection}
                            toggleComponentSection={toggleComponentSection}
                            setEditingTemplate={setEditingTemplate}
                            setEditingLifetime={setEditingLifetime}
                            setSaving={setSaving}
                            getBaseTypeTemplateCount={getBaseTypeTemplateCount}
                            buildComponentTypes={buildComponentTypes}
                        />
                    ))}
                </div>
            </div>
        </div>
        </>
    );
};

export default MaintenanceTemplatesEditor;